import React, { useState, useRef } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const PatientDataUpload = ({ patient, dataType, imagingFormat, bodyPart, clickPosition, acceptedFormats, noAcceptFilter, onDataUploaded, isAnnotation, isMap, parentDataId }) => {
    const [files, setFiles] = useState([]);
    const [description, setDescription] = useState("");
    const [isAnonymized, setIsAnonymized] = useState(false);
    const [isSynthetic, setIsSynthetic] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const dataTypeLabels = {
        imaging: "Medical Imaging",
        image: "Images",
        video: "Videos",
        document: "Documents",
        table: "Tables",
        signal: "Signals",
        pathology: "Pathology",
        ehr: "EHR"
    };

    const dataTypeIcons = {
        imaging: "fa-x-ray",
        image: "fa-image",
        video: "fa-video-camera",
        document: "fa-file-text",
        table: "fa-table",
        signal: "fa-wave-square",
        pathology: "fa-microscope",
        ehr: "fa-heartbeat"
    };

    const imagingFormatLabels = {
        nifti: "NIfTI",
        dicom: "DICOM",
        numpy: "Numpy"
    };

    const imagingFormatIcons = {
        nifti: "fa-brain",
        dicom: "fa-folder-open",
        numpy: "fa-cube"
    };

    const handleFileSelect = (e) => {
        let selectedFiles = Array.from(e.target.files);

        // For DICOM folder uploads, include all files (DICOM files often have no extension or various naming)
        // Only exclude common non-DICOM files like .txt, .xml, .json, etc.
        if (imagingFormat === 'dicom') {
            const excludedExtensions = ['.txt', '.xml', '.json', '.html', '.css', '.js', '.md', '.log', '.DS_Store'];
            selectedFiles = selectedFiles.filter(file => {
                const lowerName = file.name.toLowerCase();
                return !excludedExtensions.some(ext => lowerName.endsWith(ext)) && !lowerName.startsWith('.');
            });
        }

        // For NIfTI files, filter to only accept .nii and .nii.gz files
        if (imagingFormat === 'nifti') {
            selectedFiles = selectedFiles.filter(file => {
                const lowerName = file.name.toLowerCase();
                return lowerName.endsWith('.nii') || lowerName.endsWith('.nii.gz');
            });
            if (selectedFiles.length === 0 && e.target.files.length > 0) {
                toast.warning("Please select NIfTI files (.nii or .nii.gz)");
            }
        }

        setFiles(prev => [...prev, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        setFiles(prev => [...prev, ...droppedFiles]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (filename) => {
        const ext = filename.split('.').pop().toLowerCase();
        const iconMap = {
            // Imaging
            dcm: 'fa-file-medical',
            nii: 'fa-brain',
            // Images
            jpg: 'fa-file-image',
            jpeg: 'fa-file-image',
            png: 'fa-file-image',
            gif: 'fa-file-image',
            bmp: 'fa-file-image',
            tiff: 'fa-file-image',
            // Documents
            pdf: 'fa-file-pdf',
            doc: 'fa-file-word',
            docx: 'fa-file-word',
            txt: 'fa-file-text',
            md: 'fa-file-code',
            // Tables
            xlsx: 'fa-file-excel',
            xls: 'fa-file-excel',
            csv: 'fa-file-csv',
            // Signals
            edf: 'fa-wave-square',
            // Video
            mp4: 'fa-file-video-o',
            avi: 'fa-file-video-o',
            mov: 'fa-file-video-o',
            mkv: 'fa-file-video-o',
            wmv: 'fa-file-video-o',
            webm: 'fa-file-video-o',
        };
        return iconMap[ext] || 'fa-file';
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            toast.warning("Please select files to upload.");
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        formData.append('data_type', dataType);
        if (imagingFormat) {
            formData.append('imaging_format', imagingFormat);
        }
        // Add click position as metadata (clickPosition is the new param, bodyPart is legacy)
        if (clickPosition) {
            formData.append('metadata', JSON.stringify({ clickPosition }));
        }
        formData.append('description', description);
        formData.append('is_anonymized', isAnonymized);
        formData.append('is_synthetic', isSynthetic);
        if (isAnnotation) formData.append('is_annotation', 'true');
        if (isMap) formData.append('is_map', 'true');
        if (parentDataId) formData.append('parent_data_id', parentDataId);

        try {
            const response = await API.post(
                `/data_manager/patients/${patient.id}/upload-data/`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                }
            );

            onDataUploaded(response.data.items);
            setFiles([]);
            setDescription("");
            setIsAnonymized(false);
            setIsSynthetic(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (err) {
            console.error("Error uploading files:", err);
            const errorMessage = err.response?.data?.error || "Failed to upload files.";
            toast.error(errorMessage);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Determine the display label and icon
    const displayLabel = dataType === 'imaging' && imagingFormat
        ? `${dataTypeLabels[dataType]} - ${imagingFormatLabels[imagingFormat]}`
        : dataTypeLabels[dataType];

    const displayIcon = dataType === 'imaging' && imagingFormat
        ? imagingFormatIcons[imagingFormat]
        : dataTypeIcons[dataType];

    // Check if we're uploading a DICOM folder
    const isDicomFolder = imagingFormat === 'dicom';

    return (
        <div className="patient-data-upload">
            <div className="upload-header">
                <i className={`fa ${displayIcon} upload-icon`}></i>
                <h4>Upload {displayLabel}</h4>
            </div>

            {/* Drop Zone */}
            <div
                className="drop-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
            >
                {isDicomFolder ? (
                    <input
                        type="file"
                        ref={fileInputRef}
                        webkitdirectory=""
                        directory=""
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                ) : noAcceptFilter ? (
                    <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                ) : (
                    <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        accept={acceptedFormats}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                )}
                <i className={`fa ${isDicomFolder ? 'fa-folder-open' : 'fa-cloud-upload'} drop-icon`}></i>
                <p className="drop-text">
                    {isDicomFolder
                        ? 'Click to select a DICOM folder'
                        : 'Drag & drop files here or click to browse'
                    }
                </p>
                <p className="drop-hint">
                    {isDicomFolder
                        ? 'Select a folder containing DICOM files (any naming convention)'
                        : imagingFormat === 'nifti'
                            ? 'Accepted formats: .nii, .nii.gz'
                            : `Accepted formats: ${acceptedFormats}`
                    }
                </p>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
                <div className="selected-files">
                    <h5>Selected Files ({files.length})</h5>
                    <div className="files-list">
                        {files.map((file, index) => (
                            <div key={index} className="file-item">
                                <i className={`fa ${getFileIcon(file.name)} file-icon`}></i>
                                <div className="file-info">
                                    <span className="file-name">{file.name}</span>
                                    <span className="file-size">{formatFileSize(file.size)}</span>
                                </div>
                                <button
                                    type="button"
                                    className="btn-remove-file"
                                    onClick={() => handleRemoveFile(index)}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Upload Options */}
            <div className="upload-options">
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="form-label">Description</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter description for these files"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group mb-3">
                            <label className="form-label">&nbsp;</label>
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="anonymized"
                                    checked={isAnonymized}
                                    onChange={(e) => setIsAnonymized(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="anonymized">
                                    Anonymized Data
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group mb-3">
                            <label className="form-label">&nbsp;</label>
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="synthetic"
                                    checked={isSynthetic}
                                    onChange={(e) => setIsSynthetic(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="synthetic">
                                    Synthetic Data
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
                <div className="upload-progress">
                    <div className="progress">
                        <div
                            className="progress-bar progress-bar-striped progress-bar-animated"
                            style={{ width: `${uploadProgress}%` }}
                        >
                            {uploadProgress}%
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Button */}
            <div className="upload-actions">
                <button
                    type="button"
                    className="btn-upload"
                    onClick={handleUpload}
                    disabled={files.length === 0 || uploading}
                >
                    {uploading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Uploading...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-upload me-2"></i>
                            Upload {files.length} File{files.length !== 1 ? 's' : ''}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default PatientDataUpload;
